import axios from 'axios';
import { API_URL } from '../config/api';

// Configure axios defaults
axios.defaults.withCredentials = true;

// CSRF token cache
let csrfToken = null;

// Function to get CSRF token
const getCsrfToken = async () => {
  if (!csrfToken) {
    try {
      const response = await axios.get(`${API_URL}/api/csrf-token`);
      csrfToken = response.data.csrfToken;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch CSRF token:', error);
      }
      throw new Error('Authentication service unavailable');
    }
  }
  return csrfToken;
};

// Function to make API requests with CSRF token
export const apiRequest = async (method, url, data = null, options = {}) => {
  try {
    const token = await getCsrfToken();
    const config = {
      method,
      url: `${API_URL}${url}`,
      headers: {
        'X-CSRF-Token': token,
        'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined,
        'Content-Type': 'application/json',
        ...options.headers
      },
      timeout: 30000, // 30 second timeout
      ...options
    };
    
    if (data) {
      config.data = data;
    }
    
    return await axios(config);
  } catch (error) {
    // If CSRF token is invalid, clear it and retry once
    if (error.response?.status === 403 && /csrf/i.test(error.response?.data?.error ?? '')) {
      csrfToken = null;
      try {
        const token = await getCsrfToken();
        const config = {
          method,
          url: `${API_URL}${url}`,
          headers: {
            'X-CSRF-Token': token,
            'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined,
            'Content-Type': 'application/json',
            ...options.headers
          },
          timeout: 30000,
          ...options
        };
        
        if (data) {
          config.data = data;
        }
        
        return await axios(config);
      } catch (retryError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('API request retry failed:', retryError);
        }
        throw new Error('Request failed. Please try again.');
      }
    }
    
    // Handle different error types for production
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please try again.');
    }
    
    if (!error.response) {
      throw new Error('Network error. Please check your connection.');
    }
    
    throw error;
  }
};

// Convenience methods
export const apiGet = (url, options = {}) => apiRequest('GET', url, null, options);
export const apiPost = (url, data, options = {}) => apiRequest('POST', url, data, options);
export const apiPut = (url, data, options = {}) => apiRequest('PUT', url, data, options);
export const apiDelete = (url, options = {}) => apiRequest('DELETE', url, null, options);

// Clear CSRF token (useful for logout)
export const clearCsrfToken = () => {
  csrfToken = null;
};